// Implements: F-ITK-007, F-UIX-010, F-UIX-013
/**
 * The WhatsApp linking section (UIX.4): issue a code for a scope the person can create in, show
 * the command to send and its expiry, list the linked numbers and remove one.
 *
 * **Hidden while intake is disabled.** The list answering the unknown-route `404` is the server
 * saying so; the container then renders `whenDisabled` (the page passes its not-found page) and
 * nothing of the section. Any other failure is an `ErrorState` with a retry.
 *
 * **One person, one subtree (checklist 14).** The page keys this container by the person's id,
 * so another person signing in on the same tab never sees the previous one's code. Inside it an
 * issue answer is applied only while it is still the latest request for the selected scope: a
 * late answer for a scope the person has since changed is ignored.
 *
 * **Never resent, never stored (checklists 13, 15).** The issue is a `retry: false` POST sent
 * once per click; its code lives in this component's state only, never in the query cache or a
 * browser store. Removal asks first with `ConfirmDialog` (F-UIX-013).
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { PageHeader } from "../../../shared/ui/molecules/PageHeader";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import type { ScopeEntry } from "../../../types/api";
import { BindingList } from "../components/BindingList";
import type { BindingRow } from "../components/BindingList";
import { LinkCodeForm } from "../components/LinkCodeForm";
import type { IssuedCommandText } from "../components/LinkCodeForm";
import styles from "../components/WhatsApp.module.css";
import { isIntakeDisabled, useBindings, useIssueLinkCode, useUnlinkBinding } from "../hooks/useWhatsAppLinking";
import { bindingScopeName, defaultLinkScope, linkScopeOptions, momentText } from "../model/linking";

export interface WhatsAppLinkingContainerProps {
  /** The signed-in person, from the session. */
  readonly personId: string;
  readonly scopes: readonly ScopeEntry[];
  /** What to render instead of the section while intake is disabled. */
  readonly whenDisabled?: ReactNode;
}

export function WhatsAppLinkingContainer({
  personId,
  scopes,
  whenDisabled = null,
}: WhatsAppLinkingContainerProps): ReactElement {
  const { t } = useTranslation();
  const { push } = useToast();
  const bindings = useBindings(personId);
  const issue = useIssueLinkCode();
  const unlink = useUnlinkBinding(personId);

  const options = linkScopeOptions(scopes, t("scope.personal"));
  const [choice, setChoice] = useState<string>(defaultLinkScope(options)?.value ?? "");
  const [pending, setPending] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedCommandText | null>(null);
  const [asking, setAsking] = useState<BindingRow | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  /** The ticket of the latest issue request; an answer carrying another one is stale. */
  const latest = useRef(0);

  useEffect(
    () => () => {
      // Unmounted: whatever is still in flight belongs to nobody on screen.
      latest.current += 1;
    },
    [],
  );

  const selected = options.find((option) => option.value === choice) ?? defaultLinkScope(options);

  const changeScope = (value: string): void => {
    latest.current += 1;
    setChoice(value);
    setPending(false);
    setIssued(null);
    setIssueError(null);
  };

  const submit = async (): Promise<void> => {
    if (selected === undefined) return;
    latest.current += 1;
    const ticket = latest.current;
    setPending(true);
    setIssueError(null);
    const outcome = await issue(selected.input);
    if (ticket !== latest.current) return;
    setPending(false);
    if (outcome.ok) {
      setIssued({ command: outcome.issued.command, expires: momentText(outcome.issued.expires_at) });
      return;
    }
    setIssued(null);
    setIssueError(userMessage(outcome.error, t("intake.issue.failed")));
  };

  const confirmUnlink = async (): Promise<void> => {
    if (asking === null) return;
    const target = asking;
    setAsking(null);
    setUnlinking(true);
    const outcome = await unlink(target.id);
    setUnlinking(false);
    push(
      outcome.ok
        ? { tone: "success", message: t("intake.unlink.done") }
        : { tone: "danger", message: userMessage(outcome.error, t("intake.unlink.failed")) },
    );
  };

  if (bindings.isPending) return <LoadingState label={t("common.state.loading")} />;
  if (bindings.isError && isIntakeDisabled(bindings.error)) return <>{whenDisabled}</>;
  if (bindings.isError) {
    return (
      <ErrorState
        error={bindings.error}
        onRetry={() => {
          void bindings.refetch();
        }}
      />
    );
  }

  const rows: readonly BindingRow[] = bindings.data.map((binding) => ({
    id: binding.id,
    maskedAddress: binding.masked_address,
    scope:
      binding.scope_kind === "personal"
        ? t("intake.list.scopePersonal")
        : (bindingScopeName(binding, scopes) ?? t("intake.list.scopeUnknown")),
    linkedOn: momentText(binding.created_at),
    createdAt: binding.created_at,
  }));

  return (
    <section className={styles.section}>
      <PageHeader title={t("intake.title")} subtitle={t("intake.intro")} />
      <LinkCodeForm
        options={options}
        value={selected?.value ?? ""}
        onChange={changeScope}
        onSubmit={() => {
          void submit();
        }}
        pending={pending}
        error={issueError}
        issued={issued}
      />
      <BindingList
        rows={rows}
        disabled={unlinking}
        onUnlink={setAsking}
        refreshing={bindings.isFetching}
        onRefresh={() => {
          void bindings.refetch();
        }}
      />
      <ConfirmDialog
        open={asking !== null}
        title={t("intake.unlink.confirmTitle")}
        body={t("intake.unlink.confirmBody")}
        confirmLabel={t("common.action.confirm")}
        cancelLabel={t("common.action.cancel")}
        onConfirm={() => {
          void confirmUnlink();
        }}
        onCancel={() => {
          setAsking(null);
        }}
      />
    </section>
  );
}
